/**
 * Utility functions for AutoQM Web Application
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merges Tailwind CSS classes with conflict resolution
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format date relative to now in Korean (e.g., "2시간 전", "3일 전")
 */
export function formatDistanceToNow(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return '방금 전';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}분 전`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}시간 전`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays}일 전`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths}개월 전`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears}년 전`;
}

const dateTimeFormatter = new Intl.DateTimeFormat('ko-KR', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'Asia/Seoul',
});

const dateFormatter = new Intl.DateTimeFormat('ko-KR', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  timeZone: 'Asia/Seoul',
});

/**
 * Format date to readable string in Korean (e.g., "2025년 1월 15일 오전 10:30")
 */
export function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return dateTimeFormatter.format(d);
}

/**
 * Format date to short string in Korean (e.g., "2025년 1월 15일")
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return dateFormatter.format(d);
}

const dateTimeSecFormatter = new Intl.DateTimeFormat('ko-KR', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZone: 'Asia/Seoul',
});

/**
 * Format date with time including seconds (e.g., "2025. 01. 15. 14:30:05")
 */
export function formatDateTimeSec(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return dateTimeSecFormatter.format(d);
}

// hourCycle 'h23' (not hour12: false) so midnight is reported as 0, never 24.
const kstPartsFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  timeZone: 'Asia/Seoul',
});

export interface KstDateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * Calendar/clock components of an instant in KST (Asia/Seoul), independent of
 * the viewer's browser time zone. Returns null for empty or unparseable input.
 */
export function getKstDateParts(value: Date | string | null | undefined): KstDateParts | null {
  if (!value) return null;
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return null;

  const parts = kstPartsFormatter.formatToParts(d);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return {
    year: pick('year'),
    month: pick('month'),
    day: pick('day'),
    hour: pick('hour'),
    minute: pick('minute'),
    second: pick('second'),
  };
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * UTC instant (ISO string) → KST wall time for a datetime-local input
 * ("YYYY-MM-DDTHH:mm"). Returns '' for empty or invalid values.
 */
export function toKstDateTimeLocal(iso: string | null | undefined): string {
  const p = getKstDateParts(iso);
  if (!p) return '';
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

/**
 * datetime-local value interpreted as KST wall time → UTC ISO string.
 * Returns null for empty or invalid values.
 */
export function kstDateTimeLocalToIso(value: string): string | null {
  // Browsers emit ":ss" too when the value carries non-zero seconds.
  const match = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.exec(value);
  if (!match) return null;
  const d = new Date(`${value}${match[1] ? '' : ':00'}+09:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Truncate text to specified length with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Debounce function execution
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function execution
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Deep clone object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Generate unique ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Sleep/delay execution
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Parse error to user-friendly message.
 * Checks axios response data first, then falls back to Error.message or string.
 */
export function getErrorMessage(error: unknown, fallback = '알 수 없는 오류가 발생했습니다'): string {
  // Check axios response data first (raw AxiosError)
  const resp = (error as { response?: { data?: { error?: string; message?: string } } })?.response;
  if (resp?.data?.error) return resp.data.error;
  if (resp?.data?.message) return resp.data.message;
  // Formatted ApiError objects ({ message, status, details })
  const msg = (error as { message?: string })?.message;
  if (msg && typeof msg === 'string') return msg;
  // Standard Error objects
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return fallback;
}

/**
 * Extract error message from Axios error response.
 * Delegates to getErrorMessage for consistent extraction logic.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  return getErrorMessage(error, fallback);
}

/**
 * The workbook quota sentences, shared by the list, the create dialogs and the
 * create-error paths so the wording cannot drift. The product quota is 10 for a
 * normal member and 30 for a premium member (services/workbook_resource_safety.py).
 */
export const WORKBOOK_LIMIT_MESSAGE = '교재는 최대 30개까지 생성할 수 있습니다.';
export const WORKBOOK_LIMIT_NORMAL_MESSAGE =
  '일반회원은 교재를 최대 10개까지 생성할 수 있습니다. 사용하지 않는 교재를 삭제하거나 정회원으로 업그레이드 하세요. (정회원은 최대 30개 교재 생성 가능)';
/** Mirrors the merge route's sentence for workbook_storage_limit_exceeded (api/workbooks.py). */
export const WORKBOOK_STORAGE_LIMIT_MESSAGE =
  '휴지통에 보관 중인 교재가 너무 많아 새 교재를 만들 수 없습니다. 휴지통을 비운 뒤 다시 시도해 주세요.';

const WORKBOOK_LIMIT_PREMIUM = 30;

type WorkbookQuotaBody = { code?: unknown; limit?: unknown };

/**
 * Korean sentence for the two workbook-quota 409 codes the server emits in
 * English (workbook_limit_exceeded / workbook_storage_limit_exceeded); null for
 * any other error. The body's `limit` tells the tier apart; without a numeric
 * limit the premium sentence is kept.
 */
export function getWorkbookQuotaErrorMessage(error: unknown): string | null {
  const failure = error as { details?: WorkbookQuotaBody; response?: { data?: WorkbookQuotaBody } } | null;
  const code = failure?.details?.code ?? failure?.response?.data?.code;
  if (code === 'workbook_storage_limit_exceeded') return WORKBOOK_STORAGE_LIMIT_MESSAGE;
  if (code !== 'workbook_limit_exceeded') return null;
  const limit = failure?.details?.limit ?? failure?.response?.data?.limit;
  return typeof limit === 'number' && limit < WORKBOOK_LIMIT_PREMIUM
    ? WORKBOOK_LIMIT_NORMAL_MESSAGE
    : WORKBOOK_LIMIT_MESSAGE;
}

/**
 * Message for a failed POST /api/workbooks: the quota sentence for a quota 409,
 * otherwise what getApiErrorMessage reads.
 */
export function getWorkbookCreateErrorMessage(error: unknown, fallback: string): string {
  return getWorkbookQuotaErrorMessage(error) ?? getApiErrorMessage(error, fallback);
}

/**
 * Download blob as file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  // Firefox can drop blob downloads if the object URL is revoked immediately.
  window.setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 1000);
}

/**
 * Returns true when the current browser is known to reliably handle
 * two sequential programmatic downloads in a single user gesture.
 *
 * Verified working: Chromium-based browsers (Chrome, Edge, Whale, Opera, Brave)
 * and Safari (macOS / iOS). Firefox and other browsers frequently block the
 * second download, so the UI should fall back to a manual-click modal there.
 */
export function supportsSequentialAutoDownload(): boolean {
  if (typeof navigator === 'undefined') return true;
  const ua = navigator.userAgent || '';

  // Firefox explicitly blocks second auto-download in many configurations.
  if (/firefox/i.test(ua) || /FxiOS/i.test(ua)) return false;

  // Chromium family (Chrome, Edge, Whale, Opera, Brave, Samsung Internet, ...)
  const isChromium =
    /Chrome\//i.test(ua) ||
    /Chromium\//i.test(ua) ||
    /Edg\//i.test(ua) ||
    /OPR\//i.test(ua) ||
    /Whale\//i.test(ua) ||
    /SamsungBrowser\//i.test(ua);

  // Safari (desktop + iOS). Desktop Safari UA: "... Version/xx Safari/..." without "Chrome/".
  const isSafari = /Safari\//i.test(ua) && !/Chrome\//i.test(ua) && !/Chromium\//i.test(ua);

  return isChromium || isSafari;
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Format number with thousands separator
 */
export function formatNumber(num: number): string {
  return num.toLocaleString('en-US');
}

/**
 * Check if user is on Mac
 */
export function isMac(): boolean {
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
}

/**
 * Get modifier key symbol (Cmd for Mac, Ctrl for others)
 */
export function getModifierKey(): string {
  return isMac() ? '⌘' : 'Ctrl';
}

/**
 * Format keyboard shortcut for display
 */
export function formatShortcut(shortcut: string): string {
  return shortcut.replace('Ctrl', getModifierKey());
}
